/*
 * Switch Sale TV overlay (switch-sale-crt snippet): a WebGL port of Figma's "CRT Screen"
 * effect, drawn into each [data-switch-crt] canvas handed to SwitchSaleCRT.watch(). Also
 * the sale's grain: SwitchSaleCRT.buildNoise() sets the --ss-noise tile on <html>.
 *
 * Shared by switch-sale-controller (sale pages and product pages, loaded with defer, which
 * watches every canvas on the page) and the navs' Switch Sale callout and menu item
 * (via snippets/switch-sale-crt-loader, only the first time a menu opens). Each canvas only
 * gets a WebGL context and draws while it's on screen, so a closed menu costs nothing.
 * Loading it twice is harmless: the second copy does nothing.
 */
(function () {
  if (window.SwitchSaleCRT) return;

  // ----- TV overlay: WebGL port of Figma's "CRT Screen" effect ----------------------
  // The fragment shader below is Figma's CRT shader (WGSL) translated to GLSL ES 1.0, with
  // this page's settings baked in: aperture grille mask, mask size 12, scanlines 37%,
  // scanline size 16.5, dispersion 47% radial, flicker 62%, static 20% size 1, roll speed
  // 3.53, jitter 19%, vignette 80%, speed 0.78, curvature 0, clipped to curve. Its input is
  // the overlay gradient (data-from → data-to at data-opacity), generated in the shader
  // instead of captured from the page. The mask strength comes from data-mask,
  // data-vignette-bottom="false" fades the vignette out over the lower half of the frame,
  // and data-vignette="false" turns it off, so the lines run right to the edges (for
  // small boxes, where the vignette would otherwise blank their sides).
  //
  // One addition: on a flat gradient the shader's jitter has nothing to displace, so the
  // pixel-locked aperture grille jumps to a random offset of up to SWAY_PX either side
  // on SWAY_CHANCE of frames (picked in JS, passed in as u_sway), so the vertical lines
  // judder left and right like static. Drawn at CSS-pixel resolution (the mask and scanline sizes are in
  // CSS pixels), capped at 30fps, only while on screen, and only over the rows on screen.
  // Each canvas gets its WebGL context the first time it nears the viewport, so canvases in
  // sections hidden in this mode (or never scrolled to) cost nothing.
  var CRT_SPEED = 0.78;
  var SWAY_PX = 0.7;
  var SWAY_CHANCE = 0.7;
  var CRT_VERTEX = 'attribute vec2 a_pos; void main() { gl_Position = vec4(a_pos, 0.0, 1.0); }';
  var CRT_FRAGMENT = [
    '#ifdef GL_FRAGMENT_PRECISION_HIGH',
    'precision highp float;',
    '#else',
    'precision mediump float;',
    '#endif',
    'uniform vec2 u_canvas;',
    'uniform vec2 u_offset;',
    'uniform vec2 u_frame;',
    'uniform float u_time;',
    'uniform vec3 u_from;',
    'uniform vec3 u_to;',
    'uniform float u_alpha;',
    'uniform float u_mask;',
    'uniform float u_sway;',
    'uniform float u_vig_bottom;',
    'uniform float u_vig;',
    'const float TAU = 6.28318530718;',
    'const float PHASE120 = 2.09439510239;',
    'const float CURVATURE = 0.0;',
    'const float SCANLINES = 0.37;',
    'const float SCAN_SIZE = 16.5;',
    'const float ABERRATION = 5.64;', // 47% of 12px
    'const float FLICKER = 0.62;',
    'const float NOISE = 0.2;',
    'const float NOISE_SIZE = 1.0;',
    'const float ROLL_SPEED = 3.53;',
    'const float JITTER = 0.19;',
    'const float VIGNETTE = 0.8;',
    'const float MASK_PITCH = 12.0;',
    'const float BRIGHTNESS = 1.0;',
    'float coverage(vec2 p, vec2 texel) {',
    '  float fx = smoothstep(0.0, texel.x, p.x) * (1.0 - smoothstep(1.0 - texel.x, 1.0, p.x));',
    '  float fy = smoothstep(0.0, texel.y, p.y) * (1.0 - smoothstep(1.0 - texel.y, 1.0, p.y));',
    '  return fx * fy;',
    '}',
    'vec4 sampleRaw(vec2 p) {',
    '  p = clamp(p, vec2(0.0), vec2(1.0));',
    '  return vec4(mix(u_from, u_to, p.y) * u_alpha, u_alpha);',
    '}',
    'float hash21(vec2 p) {',
    '  vec3 h = fract(vec3(p.x, p.y, p.x) * 0.1031);',
    '  h = h + dot(h, h.yzx + 33.33);',
    '  return fract((h.x + h.y) * h.z);',
    '}',
    'float hash31(vec3 p) {',
    '  vec3 h = fract(p * vec3(0.1031, 0.1030, 0.0973));',
    '  h = h + vec3(dot(h, h.yzx + 33.33));',
    '  return fract((h.x + h.y) * h.z);',
    '}',
    'vec2 barrel(vec2 uv, vec2 aspect, float curvature, vec2 scale) {',
    '  vec2 c = (uv * 2.0 - 1.0) * aspect;',
    '  float r2 = dot(c, c);',
    '  c = c * (1.0 + curvature * 0.35 * r2) * scale;',
    '  return (c / aspect) * 0.5 + 0.5;',
    '}',
    'vec2 aberrationOffset(vec2 screenUv, vec2 aspect, float amount, vec2 texel) {',
    '  vec2 c = (screenUv * 2.0 - 1.0) * aspect;',
    '  float radius = length(c);',
    '  vec2 dir = radius > 0.0001 ? c / max(radius, 0.000001) : vec2(1.0, 0.0);',
    '  float level = clamp(radius / sqrt(2.0), 0.0, 1.0);',
    '  return dir * (amount * (0.72 + 0.48 * level)) * texel;',
    '}',
    'vec3 grilleProfile(vec3 theta, float pitch) {',
    '  float fundamentalAA = smoothstep(2.0, 3.0, pitch);',
    '  float secondAA = smoothstep(2.0, 3.0, pitch * 0.5);',
    '  return vec3(0.375) + 0.5 * fundamentalAA * cos(theta) + 0.125 * secondAA * cos(theta * 2.0);',
    '}',
    'void main() {',
    '  vec2 pix = vec2(gl_FragCoord.x, u_canvas.y - gl_FragCoord.y) + u_offset;',
    '  vec2 outDims = max(u_frame, vec2(1.0));',
    '  vec2 uv = pix / outDims;',
    '  vec2 inTexel = 1.0 / outDims;',
    '  float t = u_time;',
    '  vec2 aspectRaw = vec2(outDims.x / outDims.y, 1.0);',
    '  vec2 aspect = aspectRaw / (length(aspectRaw) / sqrt(2.0));',
    '  vec2 edgeFit = vec2(1.0 / (1.0 + CURVATURE * 0.35 * aspect.x * aspect.x), 1.0 / (1.0 + CURVATURE * 0.35 * aspect.y * aspect.y));',
    '  vec2 reserveUv = vec2(clamp(JITTER, 0.0, 1.0) * 3.9 * inTexel.x + 0.5 * inTexel.x, 0.5 * inTexel.y);',
    '  vec2 barrelScale = edgeFit / max(vec2(1.0) - 2.0 * reserveUv, vec2(0.05));',
    '  float scanAmt = SCANLINES * mix(0.6, 1.0, smoothstep(1.0, 2.5, SCAN_SIZE));',
    '  float scanNorm = 1.0 / (1.0 - scanAmt * 0.5);',
    '  vec2 warped = barrel(uv, aspect, CURVATURE, barrelScale);',
    '  float lineId = floor(warped.y * outDims.y / SCAN_SIZE);',
    '  float lineNoise = hash21(vec2(lineId, floor(t * 24.0))) - 0.5;',
    '  float drift = sin(t * 1.7 + warped.y * 9.0) * 0.5;',
    '  warped.x += JITTER * (lineNoise * 0.9 + drift * 0.4) * inTexel.x * 6.0;',
    '  float cov = coverage(warped, inTexel);',
    '  vec2 dir = aberrationOffset(uv, aspect, ABERRATION, inTexel);',
    '  vec4 sr = sampleRaw(warped + dir);',
    '  vec4 sg = sampleRaw(warped);',
    '  vec4 sb = sampleRaw(warped - dir);',
    '  float sharedAlpha = max(sr.a, max(sg.a, sb.a));',
    '  vec4 colorSample = vec4(min(vec3(sr.r, sg.g, sb.b), vec3(sharedAlpha)), sharedAlpha) * cov;',
    '  float py = warped.y * outDims.y + t * 6.0;',
    '  colorSample.rgb *= (1.0 - scanAmt * 0.5 * (1.0 - cos(TAU * py / SCAN_SIZE))) * scanNorm;',
    '  float rollPos = fract(warped.y + t * ROLL_SPEED * 0.12);',
    '  float band = smoothstep(0.0, 0.06, rollPos) * (1.0 - smoothstep(0.06, 0.22, rollPos));',
    '  colorSample.rgb *= 1.0 + band * 0.35 * step(0.001, ROLL_SPEED);',
    '  vec3 col = colorSample.rgb;',
    '  float alpha = colorSample.a;',
    '  vec2 baseWarp = barrel(uv, aspect, CURVATURE, barrelScale);',
    '  float th = (pix.x + u_sway) * (TAU / MASK_PITCH);',
    '  vec3 profile = grilleProfile(vec3(th, th - PHASE120, th + PHASE120), MASK_PITCH);',
    '  vec3 maskGain = (vec3(0.03) + 1.8 * profile) / (0.03 + 1.8 * 0.375);',
    '  col *= mix(vec3(1.0), maskGain, u_mask);',
    '  col *= 1.0 + FLICKER * (sin(t * 37.0) * 0.5 + sin(t * 11.3) * 0.3) * 0.06;',
    '  float field = floor(t * 24.0);',
    '  vec2 gp = floor(baseWarp * outDims / NOISE_SIZE);',
    '  float white = hash31(vec3(gp, field));',
    '  float hiss = hash21(vec2(floor(baseWarp.y * outDims.y / NOISE_SIZE), field * 1.7)) - 0.5;',
    '  float staticLevel = clamp(white + hiss * 0.35, 0.0, 1.0);',
    '  col = mix(col, vec3(staticLevel) * alpha, NOISE * 0.55 * alpha);',
    '  col *= 1.0 + (white - 0.5) * NOISE * 0.25;',
    '  vec2 cc = (baseWarp * 2.0 - 1.0) * aspect;',
    '  float r2 = dot(cc, cc);',
    '  float vigStrength = 3.0 * VIGNETTE * VIGNETTE * (0.35 + 0.65 * VIGNETTE);',
    '  float vigSpread = clamp(VIGNETTE * VIGNETTE * (0.6 + 0.4 * clamp(CURVATURE, 0.0, 1.0)), 0.0, 1.0);',
    '  float v = 1.0 - u_vig * vigStrength * smoothstep(mix(1.4, 0.0, vigSpread), 2.0, r2);',
    '  v = mix(v, 1.0, (1.0 - u_vig_bottom) * smoothstep(0.5, 1.0, baseWarp.y));',
    '  col = col * max(v, 0.0) * BRIGHTNESS;',
    '  float knee = alpha * 0.8;',
    '  float kneeRoom = max(alpha - knee, 0.000001);',
    '  vec3 excess = max(col - vec3(knee), vec3(0.0));',
    '  vec3 compressed = vec3(knee) + vec3(kneeRoom) * (vec3(1.0) - exp(-excess / vec3(kneeRoom)));',
    '  col = mix(col, compressed, vec3(greaterThan(col, vec3(knee))));',
    '  gl_FragColor = vec4(clamp(col, vec3(0.0), vec3(alpha)), alpha);',
    '}'
  ].join('\n');

  // How far above the visible area the CRT frame has to start for the visible top edge to
  // sit exactly where the shader's vignette (80%) begins fading in. As in Figma, whose CRT
  // layer starts above the page, the scanlines and aperture grille then run at full
  // strength from the top of the sky, the same as the gradient, instead of being faded to
  // black there. Solved by iteration because the vignette's reach depends on the frame's
  // own aspect ratio. VIGNETTE_INNER mirrors the shader: mix(1.4, 0.0, 0.8^2 * 0.6).
  var VIGNETTE_INNER = 1.4 * (1 - 0.8 * 0.8 * 0.6);

  function crtLead(width, height) {
    var lead = 0;
    for (var i = 0; i < 8; i++) {
      var frameHeight = height + lead;
      var aspectY = Math.SQRT2 / Math.sqrt(Math.pow(width / frameHeight, 2) + 1);
      var startUv = Math.max(0, (1 - Math.sqrt(VIGNETTE_INNER) / aspectY) / 2);
      lead = startUv * height / (1 - startUv);
    }
    return lead;
  }

  var crt = {
    visible: [],
    frameId: 0,
    lastDraw: 0,
    sway: 0,
    startedAt: performance.now(),
    reducedMotion: window.matchMedia('(prefers-reduced-motion: reduce)').matches,
    observer: 'IntersectionObserver' in window ? new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        var i = crt.visible.indexOf(entry.target);
        if (entry.isIntersecting && i === -1 && crt.setup(entry.target)) crt.visible.push(entry.target);
        if (!entry.isIntersecting && i !== -1) crt.visible.splice(i, 1);
      });
      crt.run();
    }, { rootMargin: '100px 0px' }) : null,

    watch: function (canvas) {
      if (canvas._ssWatched) return;
      canvas._ssWatched = true;
      if (crt.observer) crt.observer.observe(canvas);
      else if (crt.setup(canvas)) crt.visible.push(canvas);
    },

    hexToRgb: function (hex) {
      var h = String(hex || '').replace('#', '');
      if (h.length === 3) h = h.replace(/./g, '$&$&');
      var n = parseInt(h, 16) || 0;
      return [((n >> 16) & 255) / 255, ((n >> 8) & 255) / 255, (n & 255) / 255];
    },

    // Creates the canvas's WebGL context and program; returns whether it can be drawn.
    setup: function (canvas) {
      if (canvas._ssCrt) return true;
      var gl = canvas.getContext('webgl', { alpha: true, premultipliedAlpha: true, antialias: false, depth: false });
      if (!gl) return false;
      var compile = function (type, source) {
        var shader = gl.createShader(type);
        gl.shaderSource(shader, source);
        gl.compileShader(shader);
        if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
          console.warn('[Switch Sale] TV overlay shader failed:', gl.getShaderInfoLog(shader));
          return null;
        }
        return shader;
      };
      var vertex = compile(gl.VERTEX_SHADER, CRT_VERTEX);
      var fragment = compile(gl.FRAGMENT_SHADER, CRT_FRAGMENT);
      if (!vertex || !fragment) return false;
      var program = gl.createProgram();
      gl.attachShader(program, vertex);
      gl.attachShader(program, fragment);
      gl.linkProgram(program);
      if (!gl.getProgramParameter(program, gl.LINK_STATUS)) return false;
      gl.useProgram(program);
      gl.bindBuffer(gl.ARRAY_BUFFER, gl.createBuffer());
      gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 1, -1, -1, 1, -1, 1, 1, -1, 1, 1]), gl.STATIC_DRAW);
      var position = gl.getAttribLocation(program, 'a_pos');
      gl.enableVertexAttribArray(position);
      gl.vertexAttribPointer(position, 2, gl.FLOAT, false, 0, 0);
      var uniforms = {};
      ['u_canvas', 'u_offset', 'u_frame', 'u_time', 'u_from', 'u_to', 'u_alpha', 'u_mask', 'u_sway', 'u_vig_bottom', 'u_vig'].forEach(function (name) {
        uniforms[name] = gl.getUniformLocation(program, name);
      });
      gl.uniform3fv(uniforms.u_from, crt.hexToRgb(canvas.getAttribute('data-from')));
      gl.uniform3fv(uniforms.u_to, crt.hexToRgb(canvas.getAttribute('data-to')));
      gl.uniform1f(uniforms.u_alpha, parseFloat(canvas.getAttribute('data-opacity')) || 0);
      gl.uniform1f(uniforms.u_mask, parseFloat(canvas.getAttribute('data-mask')) || 0);
      gl.uniform1f(uniforms.u_vig_bottom, canvas.getAttribute('data-vignette-bottom') === 'false' ? 0 : 1);
      gl.uniform1f(uniforms.u_vig, canvas.getAttribute('data-vignette') === 'false' ? 0 : 1);
      canvas._ssCrt = { gl: gl, uniforms: uniforms };
      canvas.addEventListener('webglcontextlost', function () {
        canvas._ssCrt = null;
      });
      return true;
    },

    // clip: draw only the rows on screen, plus half a screen either side so a quick
    // scroll doesn't uncover undrawn rows before the next frame. The shader's cost grows
    // with the pixels it runs on, and the hero's canvas is several screens tall.
    draw: function (canvas, time, clip) {
      var state = canvas._ssCrt;
      if (!state || !canvas.isConnected) return;
      var width = Math.max(1, Math.round(canvas.clientWidth));
      var height = Math.max(1, Math.round(canvas.clientHeight));
      if (canvas.width !== width || canvas.height !== height) {
        canvas.width = width;
        canvas.height = height;
      }
      var gl = state.gl;
      if (clip) {
        var top = canvas.getBoundingClientRect().top;
        var margin = window.innerHeight / 2;
        var from = Math.max(0, Math.floor(-top - margin));
        var to = Math.min(height, Math.ceil(window.innerHeight - top + margin));
        if (to <= from) return;
        gl.enable(gl.SCISSOR_TEST);
        gl.scissor(0, height - to, width, to - from);
      } else {
        gl.disable(gl.SCISSOR_TEST);
      }
      var lead = canvas._ssFrame ? 0 : crtLead(width, height);
      var frame = canvas._ssFrame || [width, height + lead];
      var offset = canvas._ssOffset || [0, lead];
      gl.viewport(0, 0, width, height);
      gl.uniform2f(state.uniforms.u_canvas, width, height);
      gl.uniform2f(state.uniforms.u_offset, offset[0], offset[1]);
      gl.uniform2f(state.uniforms.u_frame, frame[0], frame[1]);
      gl.uniform1f(state.uniforms.u_time, time);
      gl.uniform1f(state.uniforms.u_sway, crt.sway);
      gl.drawArrays(gl.TRIANGLES, 0, 6);
    },

    drawAll: function (time, clip) {
      crt.visible = crt.visible.filter(function (canvas) { return canvas.isConnected; });
      crt.visible.forEach(function (canvas) { crt.draw(canvas, time, clip); });
    },

    tick: function (now) {
      crt.frameId = 0;
      if (document.hidden || !crt.visible.length) return;
      crt.frameId = requestAnimationFrame(crt.tick);
      if (now - crt.lastDraw < 33) return;
      crt.lastDraw = now;
      if (Math.random() < SWAY_CHANCE) crt.sway = (Math.random() * 2 - 1) * SWAY_PX;
      crt.drawAll(((now - crt.startedAt) * 0.001 * CRT_SPEED) % 1024, true);
    },

    run: function () {
      if (crt.reducedMotion) return crt.drawAll(0);
      if (!crt.frameId) crt.frameId = requestAnimationFrame(crt.tick);
    }
  };

  document.addEventListener('visibilitychange', function () {
    if (!document.hidden) crt.run();
  });

  // Figma's mono noise (the sale's grain): #f5e9d4 grains at up to 50% opacity, 100%
  // density, drawn once into a tile and set on <html> as --ss-noise / --ss-noise-size, for
  // backgrounds to layer over their gradient (shown at 0.5px per grain).
  var noiseStarted = false;
  function buildNoise() {
    var root = document.documentElement;
    if (noiseStarted || root.style.getPropertyValue('--ss-noise')) return;
    noiseStarted = true;
    var size = 256;
    var tile = document.createElement('canvas');
    tile.width = tile.height = size;
    var ctx = tile.getContext('2d');
    if (!ctx || !tile.toBlob) return;
    var image = ctx.createImageData(size, size);
    for (var i = 0; i < image.data.length; i += 4) {
      image.data[i] = 245;
      image.data[i + 1] = 233;
      image.data[i + 2] = 212;
      image.data[i + 3] = Math.round(Math.random() * 127.5);
    }
    ctx.putImageData(image, 0, 0);
    tile.toBlob(function (blob) {
      if (!blob) return;
      root.style.setProperty('--ss-noise', 'url(' + URL.createObjectURL(blob) + ')');
      root.style.setProperty('--ss-noise-size', size / 2 + 'px ' + size / 2 + 'px');
    });
  }

  window.SwitchSaleCRT = {
    buildNoise: buildNoise,
    watch: crt.watch,
    drawAll: crt.drawAll,
    crtLead: crtLead,
    reducedMotion: crt.reducedMotion
  };
})();
